import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { X, User, Mail, Phone, MessageSquare, Send, Loader2, ChevronDown } from "lucide-react";
import { useI18n } from "@/i18n/I18nContext";
import { PRICING_PLANS } from "@/data/content";
import { useAdminContent } from "@/lib/adminContent";
import { isZohoWebToLeadConfigured, submitZohoWebToLead } from "@/lib/zohoWebToLead";
import { lockPageScroll, unlockPageScroll } from "@/lib/scrollLock";
import SuccessAnimation from "./SuccessAnimation";
import { Link } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_CONFIGURED = Boolean(process.env.REACT_APP_BACKEND_URL?.trim());
const ORDER_MODAL_LOCK_ID = "order-modal";

// Build a categorised flat list for the select
const buildOptions = (t, plansById) => {
  const cats = ["individual", "sala", "clase", "copii", "perioada"];
  return cats.map((cat) => ({
    catId: cat,
    catLabel: t(`pricing.tabs.${cat}.label`),
    items: PRICING_PLANS[cat]
      .map((p, idx) => ({
        id: p.id,
        price: plansById?.[p.id]?.price ?? p.price,
        name: plansById?.[p.id]?.name || t(`pricing.plans.${p.id}.name`),
        order: Number.isFinite(Number(plansById?.[p.id]?.order)) ? Number(plansById[p.id].order) : idx,
      }))
      .sort((a, b) => a.order - b.order),
  }));
};

const OrderModal = ({ open, onClose, initialPlanId }) => {
  const { t, lang } = useI18n();
  const { plansById } = useAdminContent(lang);
  const [planId, setPlanId] = useState(initialPlanId || "sala-full");
  const blankForm = () => ({
    name: "",
    email: "",
    phone: "",
    note: "",
    legalConsent: false,
    website: "",
    submittedAt: Math.floor(Date.now() / 1000),
  });
  const [form, setForm] = useState(blankForm);
  const [status, setStatus] = useState("idle");
  const [errMsg, setErrMsg] = useState("");
  const [selectOpen, setSelectOpen] = useState(false);

  const groups = useMemo(() => buildOptions(t, plansById), [t, plansById]);
  const flatPlans = useMemo(() => groups.flatMap((g) => g.items), [groups]);
  const currentPlan = flatPlans.find((p) => p.id === planId) || flatPlans[0];
  const handleClose = useCallback(() => {
    setSelectOpen(false);
    onClose();
  }, [onClose]);

  useLayoutEffect(() => {
    if (open && initialPlanId) setPlanId(initialPlanId);
    if (open) {
      setForm((prev) => ({ ...prev, website: "", submittedAt: Math.floor(Date.now() / 1000) }));
      setStatus("idle");
      setErrMsg("");
      setSelectOpen(false);
      lockPageScroll(ORDER_MODAL_LOCK_ID);
    } else {
      setSelectOpen(false);
      unlockPageScroll(ORDER_MODAL_LOCK_ID);
    }
    return () => unlockPageScroll(ORDER_MODAL_LOCK_ID);
  }, [open, initialPlanId]);

  // Close on Esc
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") handleClose(); };
    if (open) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [handleClose, open]);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  // Phone: digits, +, space, parens, hyphen only
  const onPhoneChange = (e) => {
    const cleaned = e.target.value.replace(/[^\d+\s()\-]/g, "");
    setForm((f) => ({ ...f, phone: cleaned }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!currentPlan) return;
    setStatus("loading");
    setErrMsg("");
    const zoho = isZohoWebToLeadConfigured();
    if (!BACKEND_CONFIGURED && !zoho) {
      setStatus("error");
      setErrMsg(t("order.error"));
      return;
    }
    try {
      const ops = [];
      if (BACKEND_CONFIGURED) {
        ops.push(
          axios.post(`${API}/order`, {
            name: form.name,
            email: form.email,
            phone: form.phone,
            note: form.note,
            plan_id: currentPlan.id,
            plan_name: currentPlan.name,
            plan_price: currentPlan.price,
            lang,
            website: form.website,
            submittedAt: form.submittedAt,
          }),
        );
      }
      if (zoho) {
        const planSummary = `${currentPlan.name} (${currentPlan.id}) — ${currentPlan.price}`;
        const planFieldApi = process.env.REACT_APP_ZOHO_PLAN_FIELD?.trim();
        const descParts = [];
        if (!planFieldApi) {
          descParts.push(`Plan: ${currentPlan.name} (${currentPlan.id})`, `Price: ${currentPlan.price}`);
        }
        descParts.push(form.note?.trim() && `Note: ${form.note.trim()}`, `Lang: ${lang}`);
        const desc = descParts.filter(Boolean).join("\n");
        const lead = {
          "Last Name": form.name.trim(),
          Email: form.email.trim(),
          Phone: form.phone.trim(),
          Description: desc,
        };
        if (planFieldApi) {
          lead[planFieldApi] = planSummary;
        }
        const requestTypeFieldApi = process.env.REACT_APP_ZOHO_REQUEST_TYPE_FIELD?.trim();
        if (requestTypeFieldApi) {
          lead[requestTypeFieldApi] = "Subscription";
        }
        const src = process.env.REACT_APP_ZOHO_LEAD_SOURCE?.trim();
        if (src) lead.Lead_Source = src;
        ops.push(submitZohoWebToLead(lead));
      }
      const results = await Promise.allSettled(ops);
      const ok = results.some((r) => r.status === "fulfilled");
      if (!ok) {
        const first = results.find((r) => r.status === "rejected");
        throw first?.reason;
      }
      setStatus("success");
      setForm(blankForm());
    } catch (err) {
      setStatus("error");
      const detail = err?.response?.data?.detail;
      setErrMsg(Array.isArray(detail) ? (detail[0]?.msg || t("order.error")) : (detail || err?.message || t("order.error")));
    }
  };

  return (
    <AnimatePresence initial={false}>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
          className="fixed inset-0 z-[90] flex items-end sm:items-center justify-center p-0 sm:p-4"
          data-testid="order-modal"
          data-overlay-root="true"
          data-overlay-name={ORDER_MODAL_LOCK_ID}
        >
          <div
            className="absolute inset-0 bg-black/70 sm:backdrop-blur-md"
            onClick={handleClose}
            aria-hidden
          />
          <motion.div
            initial={{ y: 60, opacity: 0, scale: 0.98 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 18, opacity: 0, scale: 0.985 }}
            transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
            className="mobile-performance-surface relative w-full sm:max-w-lg glass-strong rounded-t-3xl sm:rounded-3xl max-h-[92vh] overflow-hidden flex flex-col"
            data-scroll-lock-allow="true"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-start justify-between gap-4 p-5 sm:p-7 pb-3 border-b border-[var(--border)]">
              <div className="min-w-0">
                <h3 className="font-display text-xl sm:text-2xl font-semibold leading-tight" data-testid="order-modal-title">
                  {t("order.title")}
                </h3>
                <p className="text-sm text-[var(--text-dim)] mt-1.5">{t("order.subtitle")}</p>
              </div>
              <button
                type="button"
                onClick={handleClose}
                className="w-9 h-9 rounded-full glass flex items-center justify-center hover:bg-white/10 flex-shrink-0"
                aria-label={t("ui.close")}
                data-testid="order-modal-close"
              >
                <X size={16} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto">
              {status === "success" ? (
                <SuccessAnimation message={t("order.success")} />
              ) : (
                <form onSubmit={submit} className="p-5 sm:p-7 pt-4 space-y-4" data-testid="order-form">
                  <input
                    type="text"
                    name="website"
                    value={form.website}
                    onChange={onChange}
                    autoComplete="off"
                    tabIndex={-1}
                    className="hidden"
                    aria-hidden="true"
                  />
                  {/* Plan selector */}
                  <div>
                    <label className="text-xs font-medium text-[var(--text-dim)] mb-2 block">
                      {t("order.selectLabel")}
                    </label>
                    <div className="relative">
                      <button
                        type="button"
                        onClick={() => setSelectOpen((s) => !s)}
                        className="input-field flex items-center justify-between text-left"
                        data-testid="order-plan-select"
                      >
                        <span className="flex-1 truncate">
                          <span className="font-medium">{currentPlan?.name || t("order.selectPlaceholder")}</span>
                          {currentPlan && (
                            <span className="ml-2 accent-text font-semibold">
                              {currentPlan.price} {t("order.pricePer")}
                            </span>
                          )}
                        </span>
                        <ChevronDown size={16} className={`flex-shrink-0 transition-transform ${selectOpen ? "rotate-180" : ""}`} />
                      </button>
                      <AnimatePresence>
                        {selectOpen && (
                          <motion.div
                            initial={{ opacity: 0, y: -6 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -6 }}
                            transition={{ duration: 0.18 }}
                            className="absolute z-30 mt-1.5 left-0 right-0 rounded-xl max-h-[260px] overflow-y-auto p-1.5 shadow-2xl border border-[var(--border-strong)]"
                            style={{ background: "var(--bg-soft)", backdropFilter: "blur(24px)" }}
                            data-testid="order-plan-dropdown"
                          >
                            {groups.map((g) => (
                              <div key={g.catId} className="mb-1">
                                <div className="px-3 py-2 text-[10px] tracking-[0.2em] uppercase text-[var(--text-mute)] font-semibold">
                                  {g.catLabel}
                                </div>
                                {g.items.map((it) => {
                                  const active = it.id === planId;
                                  return (
                                    <button
                                      key={it.id}
                                      type="button"
                                      onClick={() => { setPlanId(it.id); setSelectOpen(false); }}
                                      className={`w-full text-left px-3 py-2.5 rounded-lg flex items-center justify-between gap-3 hover:bg-[var(--accent)]/10 transition ${active ? "bg-[var(--accent)]/15" : ""}`}
                                      data-testid={`order-plan-option-${it.id}`}
                                    >
                                      <span className="text-sm">{it.name}</span>
                                      <span className={`text-sm font-semibold tabular-nums flex-shrink-0 ${active ? "accent-text" : "text-[var(--text-dim)]"}`}>
                                        {it.price}
                                      </span>
                                    </button>
                                  );
                                })}
                              </div>
                            ))}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  </div>

                  <div>
                    <label className="text-xs font-medium text-[var(--text-dim)] flex items-center gap-2 mb-2">
                      <User size={12} className="text-[var(--accent)]" /> {t("order.nameLabel")}
                    </label>
                    <input
                      className="input-field" name="name" required value={form.name} onChange={onChange}
                      placeholder={t("order.namePh")}
                      data-testid="order-input-name"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-medium text-[var(--text-dim)] flex items-center gap-2 mb-2">
                        <Phone size={12} className="text-[var(--accent)]" /> {t("order.phoneLabel")}
                      </label>
                      <input
                        className="input-field" name="phone" required type="tel"
                        inputMode="tel" pattern="[0-9+\s()\-]+"
                        value={form.phone} onChange={onPhoneChange}
                        placeholder={t("order.phonePh")}
                        data-testid="order-input-phone"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-[var(--text-dim)] flex items-center gap-2 mb-2">
                        <Mail size={12} className="text-[var(--accent)]" /> {t("order.emailLabel")}
                      </label>
                      <input
                        className="input-field" name="email" required type="email" value={form.email} onChange={onChange}
                        placeholder={t("order.emailPh")}
                        data-testid="order-input-email"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-xs font-medium text-[var(--text-dim)] flex items-center gap-2 mb-2">
                      <MessageSquare size={12} className="text-[var(--accent)]" /> {t("order.noteLabel")}
                    </label>
                    <textarea
                      className="input-field min-h-[90px] resize-y" name="note" value={form.note} onChange={onChange}
                      placeholder={t("order.notePh")}
                      data-testid="order-input-note"
                    />
                  </div>

                  <label className="flex items-start gap-2.5 text-xs sm:text-sm text-[var(--text-dim)]" data-testid="order-legal-consent">
                    <input
                      type="checkbox"
                      name="legalConsent"
                      checked={form.legalConsent}
                      onChange={(e) => setForm((f) => ({ ...f, legalConsent: e.target.checked }))}
                      required
                      className="mt-0.5"
                      data-testid="order-consent-checkbox"
                    />
                    <span>
                      {t("ui.legalAgree")} <Link to="/privacy-policy" className="text-[var(--accent)] hover:opacity-90">{t("footer.privacy")}</Link>,{" "}
                      <Link to="/terms" className="text-[var(--accent)] hover:opacity-90">{t("footer.terms")}</Link> {t("ui.and")}{" "}
                      <Link to="/cookies-policy" className="text-[var(--accent)] hover:opacity-90">{t("footer.cookies")}</Link>.
                    </span>
                  </label>

                  {status === "error" && (
                    <div className="text-sm text-red-400" data-testid="order-error">{errMsg}</div>
                  )}

                  <button
                    type="submit"
                    disabled={status === "loading"}
                    className="btn-primary w-full inline-flex items-center justify-center gap-2 mt-2"
                    data-testid="order-submit"
                  >
                    {status === "loading" ? (
                      <><Loader2 size={16} className="animate-spin" /> {t("order.sending")}</>
                    ) : (
                      <>{t("order.submit")} <Send size={15} /></>
                    )}
                  </button>
                </form>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default OrderModal;
